'use client';

/**
 * @brisa/ui · components/ui/tabs — shadcn Tabs (Facelift v1)
 *
 * Feature: migracao-facelift-v1 · ADR §D6 + COMPONENT_MAP §1 #10 (abas)
 * Reforma per Bruno 2026-05-15:
 *   - Track inferior h-1 (4px) cor text-08
 *   - Indicator amarelo h-1 (4px) animado com slide horizontal entre triggers
 *   - Trigger ativo text-[hsl(var(--accent-yellow))] (#FFE400)
 *   - Trigger inativo text-[hsl(var(--text-100))] (#000000)
 *   - Motion via CSS transition transform; respeita prefers-reduced-motion
 *     (já globalizado em tokens.css).
 *
 * Refactor 2026-05-16 — variant API:
 *   - 'facelift-underline' (default) — comportamento original preservado
 *   - 'default-pill' — pill ativa com bg-white + shadow; sem track/indicator
 *
 * 'use client' obrigatório (F1-RSC-03) — Radix Tabs orquestra state interno
 * e useEffect/observer pra calcular indicator.
 */
import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Variant API
// ---------------------------------------------------------------------------

export type TabsVariant = 'facelift-underline' | 'default-pill';

const TabsVariantContext = React.createContext<TabsVariant>('facelift-underline');

export type TabsProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
  variant?: TabsVariant;
};

export function Tabs({ variant = 'facelift-underline', ...props }: TabsProps) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root {...props} />
    </TabsVariantContext.Provider>
  );
}
Tabs.displayName = 'Tabs';

// ---------------------------------------------------------------------------
// TabsList
// ---------------------------------------------------------------------------

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);
  const internalRef = React.useRef<HTMLDivElement>(null);
  // Merge external + internal refs sem dep extra (forwardRef compat shadcn).
  React.useImperativeHandle(ref, () => internalRef.current as HTMLDivElement, []);

  // Indicator state — só usado no variant facelift-underline
  const [indicator, setIndicator] = React.useState<{ left: number; width: number }>(
    { left: 0, width: 0 },
  );
  const [ready, setReady] = React.useState(false);

  React.useLayoutEffect(() => {
    if (variant !== 'facelift-underline') return;

    const list = internalRef.current;
    if (!list) return;
    let raf = 0;

    function updateIndicator() {
      if (!list) return;
      const active = list.querySelector<HTMLElement>('[data-state="active"]');
      if (active) {
        const listRect = list.getBoundingClientRect();
        const triggerRect = active.getBoundingClientRect();
        setIndicator({
          left: triggerRect.left - listRect.left,
          width: triggerRect.width,
        });
        setReady(true);
      } else {
        setReady(false);
      }
    }

    // Initial sync + observe state changes nos triggers
    raf = requestAnimationFrame(updateIndicator);
    const observer = new MutationObserver(updateIndicator);
    observer.observe(list, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-state'],
    });
    // Re-calc on resize (zoom / responsive) — guard pra jsdom (sem RO API)
    const hasResizeObserver = typeof ResizeObserver !== 'undefined';
    const resizeObserver = hasResizeObserver ? new ResizeObserver(updateIndicator) : null;
    resizeObserver?.observe(list);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      resizeObserver?.disconnect();
    };
  }, [variant]);

  if (variant === 'default-pill') {
    return (
      <TabsPrimitive.List
        ref={internalRef}
        className={cn(
          // Pill container — fundo branco translúcido 10% + padding 4px all sides
          // (Bruno 2026-05-16: ajuste cor + margem da pill ativa pra borda; altura
          // auto pra respirar com o py-2 do trigger)
          'inline-flex items-center rounded-[10px] bg-white/10 p-1',
          className,
        )}
        {...props}
      >
        {children}
      </TabsPrimitive.List>
    );
  }

  // variant === 'facelift-underline' (default)
  return (
    <TabsPrimitive.List
      ref={internalRef}
      data-tab-underline=""
      className={cn(
        // Container relative — necessário pra absolute indicator
        'relative inline-flex items-center gap-6 bg-transparent text-base',
        // Track inferior (4px) cinza visível por baixo do indicator amarelo (Figma 81:70).
        // Padding bottom dá espaço pro track visualmente "abaixar" dos labels.
        'pb-1.5',
        'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:rounded-full after:bg-[hsl(var(--text-24))]',
        className,
      )}
      {...props}
    >
      {children}
      {/* Indicator amarelo — desliza animado pelo translateX */}
      <span
        aria-hidden="true"
        data-slot="tabs-indicator"
        className={cn(
          'pointer-events-none absolute bottom-0 z-10 h-1 rounded-full bg-[hsl(var(--accent-yellow))]',
          'transition-[transform,width] duration-base ease-out',
          !ready && 'opacity-0',
        )}
        style={{
          width: indicator.width,
          transform: `translateX(${indicator.left}px)`,
        }}
      />
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

// ---------------------------------------------------------------------------
// TabsTrigger
// ---------------------------------------------------------------------------

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);

  if (variant === 'default-pill') {
    return (
      <TabsPrimitive.Trigger
        ref={ref}
        className={cn(
          // Base pill trigger — py-2 + px-10 (Bruno 2026-05-16: respiro vertical
          // 0.5rem e horizontal 2.5rem pra pill ficar mais larga)
          'inline-flex items-center justify-center whitespace-nowrap rounded-[8px] px-10 py-2',
          'font-archivo text-sm font-medium leading-none text-[hsl(var(--text-100))]',
          'transition-colors duration-base ease-out',
          // Active — pill branca com shadow
          'data-[state=active]:bg-white data-[state=active]:shadow-sm',
          // Inactive — transparente
          'data-[state=inactive]:bg-transparent',
          // a11y
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
        {...props}
      />
    );
  }

  // variant === 'facelift-underline' (default)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      data-tab-underline=""
      className={cn(
        // base — text inactive (preto), padding bottom espaço pro track 4px
        'relative inline-flex items-center justify-center whitespace-nowrap px-1 pb-3 pt-2 ' +
          'text-base font-semibold leading-none text-[hsl(var(--text-100))] ' +
          'transition-colors duration-base ease-out ' +
          // estado ativo — text amarelo (indicator desliza via TabsList span)
          'data-[state=active]:text-[hsl(var(--accent-yellow))] ' +
          // a11y
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
          'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

// ---------------------------------------------------------------------------
// TabsContent — inalterado
// ---------------------------------------------------------------------------

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
